import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Menu,
  Checkbox,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  GroupWork as BenchIcon,
  Share as ShareIcon,
  PersonAdd as PersonAddIcon,
  PictureAsPdf as PdfIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { fetchBrandingLogoAbsoluteUrl } from '../../utils/brandingLogo';
import { useAuth } from '../../context/AuthContext';
import BenchShareDialog from './BenchShareDialog';
import AvailableCandidatesSnippet from './AvailableCandidatesSnippet';
import {
  canEditBenchMeta,
  canDeleteBench,
  canShareBench,
  benchTypeLabel,
  benchNameSx,
} from './benchSalesPermissions';

const BenchesList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();

  const listVariant = useMemo(() => {
    if (location.pathname.startsWith('/my-items/my-benches')) return 'mine';
    if (location.pathname.startsWith('/my-team/bench')) return 'team';
    return 'all';
  }, [location.pathname]);

  const listScope = listVariant === 'mine' ? 'mine' : listVariant === 'team' ? 'team' : 'all';

  const pageTitle =
    listVariant === 'mine' ? 'My Benches' : listVariant === 'team' ? 'My Team Bench' : 'All Benches';
  const pageSubtitle =
    listVariant === 'mine'
      ? 'Benches you created'
      : listVariant === 'team'
        ? 'Benches owned by your direct reports'
        : 'Organization benches (visibility follows your role)';

  const [benches, setBenches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    reference: true,
    name: true,
    type: true,
    candidates: true,
    shared: true,
    status: true,
    createdBy: true,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedBench, setSelectedBench] = useState(null);
  const [flowerOptions, setFlowerOptions] = useState([]);
  /** Primary / secondary domains + company name for bench naming UI */
  const [benchNamingMeta, setBenchNamingMeta] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    bench_kind: 'user',
    nameMode: 'flower',
    flower_name: null,
    custom_suffix: '',
    domain_choice: 'primary',
    company_domain_id: '',
    status: 'active',
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, bench: null });
  const [addCandidateDialog, setAddCandidateDialog] = useState(false);
  const [addCandidateBench, setAddCandidateBench] = useState(null);
  const [availableCandidates, setAvailableCandidates] = useState([]);
  const [selectedContactForAdd, setSelectedContactForAdd] = useState(null);
  const [addCandidateForm, setAddCandidateForm] = useState({
    availability_status: 'available',
    block_other_benches: false,
  });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shareBenchId, setShareBenchId] = useState(null);

  useEffect(() => {
    fetchBenches();
  }, [filterType, listScope]);

  const fetchBenches = async () => {
    try {
      setLoading(true);
      const params = { scope: listScope };
      if (filterType !== 'all') {
        params.type = filterType;
      }
      const response = await api.get('/bench-sales', { params });
      const data = response.data?.data;
      const list = Array.isArray(data) ? data : data?.benches || [];
      setBenches(list);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch benches');
      setBenches([]);
    } finally {
      setLoading(false);
    }
  };

  const loadBenchNamingMeta = async () => {
    try {
      const res = await api.get('/bench-sales/meta/bench-naming');
      const d = res.data?.data || res.data || {};
      setFlowerOptions(Array.isArray(d.flowers) ? d.flowers : []);
      setBenchNamingMeta({
        primary_domain: d.primary_domain || null,
        secondary_domains: Array.isArray(d.secondary_domains) ? d.secondary_domains : [],
        company_name: d.company_name || '',
      });
    } catch {
      setFlowerOptions([]);
      setBenchNamingMeta(null);
    }
  };

  const handleOpenCreate = () => {
    setDialogMode('create');
    setSelectedBench(null);
    setFormData({
      description: '',
      bench_kind: 'user',
      nameMode: 'flower',
      flower_name: null,
      custom_suffix: '',
      domain_choice: 'primary',
      company_domain_id: '',
      status: 'active',
    });
    loadBenchNamingMeta();
    setOpenDialog(true);
  };

  const handleOpenEdit = (bench) => {
    if (!canEditBenchMeta(user, isAdmin, bench)) return;
    setDialogMode('edit');
    setSelectedBench(bench);
    setFormData({
      description: bench.description || '',
      bench_kind: 'user',
      nameMode: 'flower',
      flower_name: null,
      custom_suffix: '',
      domain_choice: 'primary',
      company_domain_id: '',
      status: bench.status || 'active',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBench(null);
    setFormData({
      description: '',
      bench_kind: 'user',
      nameMode: 'flower',
      flower_name: null,
      custom_suffix: '',
      domain_choice: 'primary',
      company_domain_id: '',
      status: 'active',
    });
  };

  const createFormValid = () => {
    if (formData.nameMode === 'custom') {
      const t = (formData.custom_suffix || '').trim();
      if (t.length < 2) return false;
    } else if (!formData.flower_name) {
      return false;
    }
    if (formData.domain_choice === 'primary' && !benchNamingMeta?.primary_domain) {
      return false;
    }
    if (formData.domain_choice === 'secondary') {
      if (!(benchNamingMeta?.secondary_domains || []).length) return false;
      if (!formData.company_domain_id) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      setError('');
      if (dialogMode === 'create') {
        const payload = {
          description: formData.description || undefined,
        };
        if (isAdmin) {
          payload.bench_kind = formData.bench_kind;
        }
        if (formData.nameMode === 'custom') {
          const t = (formData.custom_suffix || '').trim();
          if (t.length < 2) {
            setError('Enter a custom name (at least 2 characters).');
            return;
          }
          payload.custom_suffix = t;
        } else {
          const f =
            typeof formData.flower_name === 'string'
              ? formData.flower_name
              : formData.flower_name || '';
          if (!f) {
            setError('Choose a flower from the list.');
            return;
          }
          payload.flower_name = f;
        }
        payload.domain_choice = formData.domain_choice || 'primary';
        if (formData.domain_choice === 'secondary' && formData.company_domain_id) {
          payload.company_domain_id = formData.company_domain_id;
        }
        await api.post('/bench-sales', payload);
        setSuccess('Bench created successfully');
      } else {
        await api.put(`/bench-sales/${selectedBench.id}`, {
          description: formData.description || undefined,
          status: formData.status,
        });
        setSuccess('Bench updated successfully');
      }
      handleCloseDialog();
      fetchBenches();
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/bench-sales/${deleteDialog.bench.id}`);
      setSuccess('Bench deleted successfully');
      setDeleteDialog({ open: false, bench: null });
      fetchBenches();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete bench');
    }
  };

  const filteredBenches = benches.filter(
    (bench) =>
      bench.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bench.bench_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bench.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  const getBenchTypeChipColor = (type) => {
    if (type === 'admin') return 'primary';
    if (type === 'manager') return 'warning';
    return 'secondary';
  };

  const visibleColCount =
    (visibleCols.reference ? 1 : 0) +
    (visibleCols.name ? 1 : 0) +
    (visibleCols.type ? 1 : 0) +
    (visibleCols.candidates ? 1 : 0) +
    (visibleCols.shared ? 1 : 0) +
    (visibleCols.status ? 1 : 0) +
    (visibleCols.createdBy ? 1 : 0) +
    1;

  const handleOpenAddCandidate = async (bench) => {
    setAddCandidateBench(bench);
    setSelectedContactForAdd(null);
    setAddCandidateForm({ availability_status: 'available', block_other_benches: false });
    try {
      const res = await api.get('/bench-sales/candidates/available', {
        params: { limit: 200, bench_id: bench.id },
      });
      const data = res.data?.data || res.data;
      setAvailableCandidates(data?.candidates || data || []);
    } catch (e) {
      setAvailableCandidates([]);
    }
    setAddCandidateDialog(true);
  };

  const handleAddCandidateSubmit = async () => {
    if (!addCandidateBench || !selectedContactForAdd) return;
    try {
      const { block_other_benches: blockOther, ...rest } = addCandidateForm;
      await api.post('/bench-sales/candidates', {
        bench_id: addCandidateBench.id,
        contact_id: selectedContactForAdd.id,
        ...rest,
        ...(isAdmin && blockOther ? { block_other_benches: true } : {}),
      });
      setSuccess('Candidate added to bench');
      setAddCandidateDialog(false);
      setAddCandidateBench(null);
      fetchBenches();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add candidate');
    }
  };

  const handlePrintBenchPdf = async (bench) => {
    if (!bench?.id) {
      setError('Invalid bench');
      return;
    }
    setPdfLoading(true);
    setError('');
    try {
      const res = await api.get(`/bench-sales/${bench.id}`);
      const benchData = res.data?.data ?? res.data;
      const rawCandidates = benchData?.candidates ?? [];
      const candidates = Array.isArray(rawCandidates) ? rawCandidates : [];
      const shortName = (fn, ln) => `${String(fn || '').slice(0, 3)} ${String(ln || '').slice(0, 3)}`;
      const esc = (s) => String(s ?? '').replace(/</g, '&lt;').replace(/"/g, '&quot;');
      const escAttr = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      let logoUrl = null;
      try {
        logoUrl = await fetchBrandingLogoAbsoluteUrl();
      } catch {
        logoUrl = null;
      }
      const logoBlock = logoUrl
        ? `<div style="margin-bottom:16px"><img src="${escAttr(logoUrl)}" alt="" style="max-height:56px;max-width:260px;object-fit:contain" /></div>`
        : '';
      const rows = candidates.map((bc, i) => {
        const contact = bc.contact ?? bc;
        return `<tr><td>${i + 1}</td><td>${shortName(contact.first_name, contact.last_name)}</td><td>${esc(contact.email)}</td><td>${esc(bc.availability_status)}</td><td>${bc.expected_rate ? '$' + bc.expected_rate : '-'}</td></tr>`;
      }).join('');
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError('Please allow pop-ups to print PDF');
        return;
      }
      printWindow.document.write(`
        <!DOCTYPE html><html><head><title>Bench: ${esc(benchData?.name ?? bench.name)}</title>
        <style>body{font-family:system-ui,sans-serif;padding:20px;} table{border-collapse:collapse;width:100%;} th,td{border:1px solid #ddd;padding:8px;text-align:left;} th{background:#f5f5f5;}</style></head><body>
        ${logoBlock}
        <h1>${esc(benchData?.name ?? bench.name ?? 'Bench')}</h1>
        <p>Reference: ${esc(benchData?.bench_code ?? bench.bench_code)} &nbsp;|&nbsp; Candidates: ${candidates.length}</p>
        <table><thead><tr><th>#</th><th>Name (Short)</th><th>Email</th><th>Status</th><th>Expected Rate</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message;
      if (status === 403) {
        setError("You don't have access to this bench. Only the owner or shared benches can be exported.");
      } else if (status === 404) {
        setError('Bench not found.');
      } else if (msg) {
        setError(msg);
      } else {
        setError('Failed to load bench for PDF. Check your connection and try again.');
      }
    } finally {
      setPdfLoading(false);
    }
  };

  const goToBench = (bench) => {
    navigate(`/bench-sales/${bench.id}`, { state: { listPath: location.pathname } });
  };

  const showCreate = listVariant !== 'team';

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            {pageTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pageSubtitle}
          </Typography>
        </Box>
        {showCreate ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create Bench
          </Button>
        ) : null}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search benches..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Bench kind</InputLabel>
            <Select
              value={filterType}
              label="Bench kind"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">All kinds</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon fontSize="small" />}
            onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
            type="button"
          >
            Columns
          </Button>
          <Menu
            anchorEl={colMenuAnchorEl}
            open={Boolean(colMenuAnchorEl)}
            onClose={() => setColMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {[
              ['reference', 'Reference'],
              ['name', 'Name'],
              ['type', 'Type'],
              ['candidates', 'Candidates'],
              ['shared', 'Shared'],
              ['status', 'Status'],
              ['createdBy', 'Created By'],
            ].map(([key, label]) => (
              <MenuItem
                key={key}
                onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
                dense
              >
                <Checkbox size="small" checked={Boolean(visibleCols[key])} />
                <Typography variant="body2">{label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {visibleCols.reference && <TableCell>Reference</TableCell>}
                    {visibleCols.name && <TableCell>Name</TableCell>}
                    {visibleCols.type && <TableCell>Type</TableCell>}
                    {visibleCols.candidates && <TableCell>Candidates</TableCell>}
                    {visibleCols.shared && <TableCell>Shared</TableCell>}
                    {visibleCols.status && <TableCell>Status</TableCell>}
                    {visibleCols.createdBy && <TableCell>Created By</TableCell>}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBenches
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((bench) => (
                      <TableRow
                        key={bench.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => goToBench(bench)}
                      >
                        {visibleCols.reference && (
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {bench.bench_code}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleCols.name && (
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BenchIcon color="primary" fontSize="small" />
                              <Typography component="span" variant="body2" sx={benchNameSx(bench.bench_type)}>
                                {bench.name}
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                        {visibleCols.type && (
                          <TableCell>
                            <Chip
                              label={benchTypeLabel(bench.bench_type)}
                              size="small"
                              color={getBenchTypeChipColor(bench.bench_type)}
                            />
                          </TableCell>
                        )}
                        {visibleCols.candidates && (
                          <TableCell>{bench.candidateCount ?? bench.candidate_count ?? 0}</TableCell>
                        )}
                        {visibleCols.shared && (
                          <TableCell>
                            {bench.is_shared && (
                              <Tooltip title="Shared">
                                <ShareIcon color="action" fontSize="small" />
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                        {visibleCols.status && (
                          <TableCell>
                            <Chip
                              label={bench.status}
                              size="small"
                              color={getStatusColor(bench.status)}
                            />
                          </TableCell>
                        )}
                        {visibleCols.createdBy && (
                          <TableCell>
                            {bench.creator?.first_name} {bench.creator?.last_name}
                          </TableCell>
                        )}
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Add Candidate">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenAddCandidate(bench)}
                            >
                              <PersonAddIcon />
                            </IconButton>
                          </Tooltip>
                          {canShareBench(user, isAdmin, bench) ? (
                            <Tooltip title="Share by email">
                              <IconButton size="small" onClick={() => setShareBenchId(bench.id)}>
                                <ShareIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Export PDF (short names)">
                            <IconButton
                              size="small"
                              disabled={pdfLoading}
                              onClick={() => handlePrintBenchPdf(bench)}
                            >
                              <PdfIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => goToBench(bench)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {canEditBenchMeta(user, isAdmin, bench) ? (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenEdit(bench)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {canDeleteBench(user, isAdmin, bench) ? (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, bench })}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredBenches.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={visibleColCount} align="center">
                        <Typography color="text.secondary" py={4}>
                          No benches found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredBenches.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      <AvailableCandidatesSnippet />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Create Bench' : 'Edit Bench'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {dialogMode === 'create' && (
              <>
                {!benchNamingMeta?.primary_domain && formData.domain_choice === 'primary' ? (
                  <Alert severity="error">
                    Primary Domain is missing. Please define Primary domain{' '}
                    <Link component={RouterLink} to="/admin/company-domains" sx={{ fontWeight: 600 }}>
                      here
                    </Link>
                    .
                  </Alert>
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  Name format:{' '}
                  <strong>{'{Company}-{domain}-{Admin|User|Manager}-Bench-{suffix}'}</strong> — company name comes from
                  branding; the domain segment uses your primary domain by default (hostname normalized with hyphens). Pick
                  a flower or enter a custom suffix.
                </Typography>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Domain segment in bench name
                  </Typography>
                  <RadioGroup
                    value={formData.domain_choice}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        domain_choice: e.target.value,
                        company_domain_id: '',
                      })
                    }
                  >
                    <FormControlLabel
                      value="primary"
                      control={<Radio size="small" />}
                      label={
                        benchNamingMeta?.primary_domain
                          ? `Primary domain (${benchNamingMeta.primary_domain.domain})`
                          : 'Primary domain (not configured)'
                      }
                    />
                    <FormControlLabel
                      value="secondary"
                      disabled={!(benchNamingMeta?.secondary_domains || []).length}
                      control={<Radio size="small" />}
                      label={
                        (benchNamingMeta?.secondary_domains || []).length
                          ? 'Secondary domain (choose below)'
                          : 'Secondary domain (add non-primary domains under Administration → Company domains)'
                      }
                    />
                  </RadioGroup>
                  {formData.domain_choice === 'secondary' ? (
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                      <InputLabel id="bench-secondary-domain-label">Secondary domain</InputLabel>
                      <Select
                        labelId="bench-secondary-domain-label"
                        label="Secondary domain"
                        value={formData.company_domain_id}
                        onChange={(e) => setFormData({ ...formData, company_domain_id: e.target.value })}
                      >
                        {(benchNamingMeta?.secondary_domains || []).map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.domain}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                </Box>
                <RadioGroup
                  row
                  value={formData.nameMode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      nameMode: e.target.value,
                      flower_name: null,
                      custom_suffix: '',
                    })
                  }
                >
                  <FormControlLabel value="flower" control={<Radio />} label="Choose from flower list" />
                  <FormControlLabel value="custom" control={<Radio />} label="Custom suffix" />
                </RadioGroup>
                {formData.nameMode === 'flower' ? (
                  <Autocomplete
                    options={flowerOptions}
                    value={formData.flower_name}
                    onChange={(e, v) => setFormData({ ...formData, flower_name: v })}
                    ListboxProps={{ style: { maxHeight: 280 } }}
                    renderInput={(params) => (
                      <TextField {...params} label="Flower name (100 options)" placeholder="Search..." />
                    )}
                  />
                ) : (
                  <TextField
                    label="Custom suffix"
                    fullWidth
                    required
                    placeholder="e.g. Phoenix Team or Project Alpha"
                    value={formData.custom_suffix}
                    onChange={(e) => setFormData({ ...formData, custom_suffix: e.target.value })}
                    helperText="2–80 characters, letters and numbers (server may strip some symbols)."
                  />
                )}
                {isAdmin && (
                  <FormControl>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Bench kind (Administrator)
                    </Typography>
                    <RadioGroup
                      row
                      value={formData.bench_kind}
                      onChange={(e) => setFormData({ ...formData, bench_kind: e.target.value })}
                    >
                      <FormControlLabel value="user" control={<Radio />} label="User Bench" />
                      <FormControlLabel value="admin" control={<Radio />} label="Admin Bench (locked)" />
                    </RadioGroup>
                  </FormControl>
                )}
              </>
            )}
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            {dialogMode === 'edit' && (
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={dialogMode === 'create' && !createFormValid()}
          >
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, bench: null })}>
        <DialogTitle>Delete Bench</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{deleteDialog.bench?.name}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, bench: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addCandidateDialog}
        onClose={() => {
          setAddCandidateDialog(false);
          setAddCandidateBench(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Candidate to {addCandidateBench?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={availableCandidates}
              getOptionLabel={(opt) =>
                `${opt.first_name || ''} ${opt.last_name || ''} (${opt.email || ''})`
              }
              value={selectedContactForAdd}
              onChange={(e, v) => setSelectedContactForAdd(v)}
              renderInput={(params) => <TextField {...params} label="Select Candidate" />}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={addCandidateForm.availability_status}
                label="Status"
                onChange={(e) =>
                  setAddCandidateForm({ ...addCandidateForm, availability_status: e.target.value })
                }
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="interviewing">Interviewing</MenuItem>
                <MenuItem value="offered">Offered</MenuItem>
                <MenuItem value="placed">Placed</MenuItem>
                <MenuItem value="not_available">Not Available</MenuItem>
              </Select>
            </FormControl>
            {isAdmin ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(addCandidateForm.block_other_benches)}
                    onChange={(e) =>
                      setAddCandidateForm({ ...addCandidateForm, block_other_benches: e.target.checked })
                    }
                  />
                }
                label="Block this candidate from being added to other benches (exclusive to this bench)"
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddCandidateDialog(false);
              setAddCandidateBench(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddCandidateSubmit} disabled={!selectedContactForAdd}>
            Add to Bench
          </Button>
        </DialogActions>
      </Dialog>

      <BenchShareDialog
        open={Boolean(shareBenchId)}
        benchId={shareBenchId}
        listPath={location.pathname}
        onClose={(ok) => {
          setShareBenchId(null);
          if (ok) setSuccess('Share email sent');
        }}
      />
    </Box>
  );
};

export default BenchesList;
